import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { X, Send, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import { CONTACT, ROOMS_META } from "../data/site";
import { api } from "../lib/adminApi";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.003 3C9.373 3 3.99 8.383 3.99 15.013c0 2.117.553 4.184 1.605 6.008L3.9 28.1l7.24-1.897a12.02 12.02 0 0 0 4.86 1.02h.004c6.63 0 12.013-5.383 12.013-12.013C28.017 8.383 22.633 3 16.003 3zm0 21.99h-.003a9.96 9.96 0 0 1-4.548-1.11l-.326-.194-4.297 1.127 1.147-4.19-.212-.335a9.93 9.93 0 0 1-1.523-5.27c0-5.514 4.487-10 10.006-10 5.517 0 10 4.486 10 10.001 0 5.514-4.483 9.97-10 9.97zm5.494-7.463c-.3-.15-1.78-.878-2.056-.978-.276-.1-.477-.15-.678.15-.2.3-.777.978-.953 1.178-.175.2-.35.226-.65.075-.3-.15-1.27-.468-2.42-1.492-.894-.797-1.498-1.782-1.673-2.082-.175-.3-.019-.462.132-.611.135-.135.3-.35.45-.526.15-.175.2-.3.3-.5.1-.2.05-.376-.025-.526-.075-.15-.677-1.632-.928-2.235-.244-.586-.492-.507-.677-.516l-.577-.01c-.2 0-.526.075-.802.376s-1.053 1.028-1.053 2.51 1.078 2.912 1.228 3.112c.15.2 2.122 3.24 5.14 4.544.718.31 1.278.494 1.715.632.72.229 1.376.196 1.894.119.578-.086 1.78-.727 2.03-1.43.25-.703.25-1.305.175-1.43-.075-.126-.276-.2-.576-.35z" />
  </svg>
);

const emptyForm = { name: "", email: "", phone: "", checkin: "", checkout: "", guests: "2", room: "", message: "" };

export const BookNowModal = () => {
  const { t } = useLang();
  const { open, setOpen, preselect } = useBooking();
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(false);
  const [availLoading, setAvailLoading] = useState(false);
  const [avail, setAvail] = useState(null); // { ok: bool, room: name|null } | { rangeError: true } | null
  const [pricing, setPricing] = useState(null); // { rate, rooms:{id:{nights,total,avg}} }

  // Today (guest local date) — used to block past dates in the pickers.
  const todayISO = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  // Map the room name shown in the select back to its backend room id.
  const roomIdByName = useMemo(() => {
    const map = {};
    t.rooms.list.forEach((r, i) => {
      if (ROOMS_META[i] && !ROOMS_META[i].hidden) map[r.name] = ROOMS_META[i].id;
    });
    return map;
  }, [t]);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...emptyForm, room: preselect || "" }));
      setError(false);
      setAvail(null);
    }
  }, [open, preselect]);

  // Live availability check against the admin calendar whenever dates / room change.
  useEffect(() => {
    const { checkin, checkout, room } = form;
    if (!checkin || !checkout) {
      setAvail(null);
      return;
    }
    if (checkout <= checkin) {
      setAvail({ rangeError: true });
      return;
    }
    let cancelled = false;
    setAvailLoading(true);
    Promise.all([api.checkAvailability(checkin, checkout), api.getPricing(checkin, checkout).catch(() => null)])
      .then(([data, price]) => {
        if (cancelled) return;
        if (price) setPricing(price);
        if (room && roomIdByName[room]) {
          const a = data[roomIdByName[room]] ?? 0;
          setAvail({ ok: a > 0, room });
        } else {
          const anyAvail = Object.values(data || {}).some((v) => v > 0);
          setAvail({ ok: anyAvail, room: null });
        }
      })
      .catch(() => {
        if (!cancelled) setAvail(null); // never block on a network hiccup
      })
      .finally(() => {
        if (!cancelled) setAvailLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.checkin, form.checkout, form.room, roomIdByName]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const soldOut = avail && avail.ok === false;
  const rangeError = avail && avail.rangeError;
  const selRoomId = form.room ? roomIdByName[form.room] : null;
  const priceInfo = pricing && selRoomId ? pricing.rooms?.[selRoomId] : null;
  const rate = pricing?.rate || 0;
  const cordoba = (usd) => `C$${Math.round(Number(usd || 0) * rate).toLocaleString()}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError(true);
      return;
    }
    if (rangeError) {
      toast.error(t.book.availRangeError || "Check-out must be after check-in.");
      return;
    }
    if (soldOut) {
      toast.error(
        t.book.availSoldOut ||
          "Sorry, those dates are fully booked. Please try different dates or message us on WhatsApp."
      );
      return;
    }
    // Save the lead to the backend (fire-and-forget; never blocks WhatsApp)
    api
      .createLead({
        name: form.name,
        email: form.email,
        phone: form.phone,
        checkin: form.checkin,
        checkout: form.checkout,
        guests: form.guests,
        room: form.room,
        message: form.message,
      })
      .catch(() => {});
    const b = t.book;
    const lines = [
      b.wa_intro,
      "",
      `${b.wa_name}: ${form.name}`,
      form.checkin ? `${b.wa_checkin}: ${form.checkin}` : null,
      form.checkout ? `${b.wa_checkout}: ${form.checkout}` : null,
      `${b.wa_guests}: ${form.guests}`,
      form.room ? `${b.wa_room}: ${form.room}` : null,
      form.message ? `${b.wa_msg}: ${form.message}` : null,
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${text}`, "_blank");
    toast.success(t.book.submit, { description: CONTACT.whatsappDisplay });
    setOpen(false);
  };

  const handleWhatsApp = () => {
    const b = t.book;
    const lines = [b.wa_intro];
    if (form.room) lines.push(`${b.wa_room}: ${form.room}`);
    if (form.name.trim()) lines.push(`${b.wa_name}: ${form.name}`);
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${text}`, "_blank");
    setOpen(false);
  };

  const inputCls =
    "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-800 outline-none transition-colors placeholder:text-stone-400 focus:border-purpleink focus:ring-2 focus:ring-purpleink/20";
  const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-stone-500";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-[calc(100vw-1.5rem)] gap-0 overflow-y-auto overflow-x-hidden rounded-3xl border-0 bg-sand p-0 sm:max-w-lg [&>button:last-child]:hidden"
        data-testid="booking-modal"
      >
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-3xl bg-purpleink-deep px-5 pb-7 pt-6 grain sm:px-7 sm:pb-8 sm:pt-7">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-marigold/20 blur-2xl" />
          <button
            onClick={() => setOpen(false)}
            data-testid="booking-close"
            className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <h2 className="relative font-display text-2xl font-bold text-white sm:text-3xl">{t.book.title}</h2>
          <DialogTitle className="sr-only">{t.book.title}</DialogTitle>
          <p className="relative mt-2 max-w-sm text-sm text-white/75">{t.book.sub}</p>
          <DialogDescription className="sr-only">{t.book.sub}</DialogDescription>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-5 py-6 sm:gap-5 sm:px-7 sm:py-7" data-testid="booking-form">
          <div>
            <label className={labelCls} htmlFor="bk-name">{t.book.name}</label>
            <input
              id="bk-name"
              data-testid="booking-name"
              value={form.name}
              onChange={set("name")}
              placeholder={t.book.namePh}
              className={`${inputCls} ${error ? "border-red-400 ring-2 ring-red-100" : ""}`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className={labelCls} htmlFor="bk-email">{t.book.email || "Email"}</label>
              <input
                id="bk-email"
                type="email"
                data-testid="booking-email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@email.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-phone">{t.book.phone || "Phone / WhatsApp"}</label>
              <input
                id="bk-phone"
                type="tel"
                data-testid="booking-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+1 555 000 0000"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className={labelCls} htmlFor="bk-in">{t.book.checkin}</label>
              <div className="relative">
                <input
                  id="bk-in"
                  type="date"
                  data-testid="booking-checkin"
                  min={todayISO}
                  value={form.checkin}
                  onChange={set("checkin")}
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-out">{t.book.checkout}</label>
              <input
                id="bk-out"
                type="date"
                data-testid="booking-checkout"
                min={form.checkin || todayISO}
                value={form.checkout}
                onChange={set("checkout")}
                className={inputCls}
              />
            </div>
          </div>

          {/* Live availability against the admin booking calendar */}
          {(availLoading || avail) && (
            <div
              data-testid="booking-availability"
              className={`flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-medium ${
                availLoading
                  ? "bg-stone-100 text-stone-500"
                  : rangeError || soldOut
                    ? "bg-red-50 text-red-700"
                    : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {availLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t.book.availChecking || "Checking availability…"}
                </>
              ) : rangeError ? (
                <>
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {t.book.availRangeError || "Check-out must be after check-in."}
                </>
              ) : soldOut ? (
                <>
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {avail.room
                    ? (t.book.availRoomFull || "{room} is fully booked for these nights. Try different dates or chat with us.").replace("{room}", avail.room)
                    : (t.book.availAllFull || "We're fully booked for these nights. Try different dates or chat with us on WhatsApp.")}
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  {avail.room
                    ? (t.book.availRoomOk || "{room} is available for your dates!").replace("{room}", avail.room)
                    : (t.book.availAllOk || "Rooms are available for your dates!")}
                </>
              )}
            </div>
          )}

          {/* Live price for the selected room + dates */}
          {priceInfo && !rangeError && !soldOut && (
            <div data-testid="booking-price" className="flex items-center justify-between rounded-xl bg-purpleink/[0.06] px-3.5 py-3">
              <div>
                <p className="text-sm font-semibold text-purpleink-deep">
                  ${priceInfo.avg}
                  <span className="text-xs font-normal text-stone-500"> / night · {cordoba(priceInfo.avg)}</span>
                </p>
                <p className="text-xs text-stone-500">{priceInfo.nights} {priceInfo.nights === 1 ? "night" : "nights"}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-purpleink-deep" data-testid="booking-price-total">${priceInfo.total}</p>
                <p className="text-xs text-stone-500">{cordoba(priceInfo.total)} total</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className={labelCls} htmlFor="bk-guests">{t.book.guests}</label>
              <select
                id="bk-guests"
                data-testid="booking-guests"
                value={form.guests}
                onChange={set("guests")}
                className={inputCls}
              >
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-room">{t.book.room}</label>
              <select
                id="bk-room"
                data-testid="booking-room"
                value={form.room}
                onChange={set("room")}
                className={inputCls}
              >
                <option value="">{t.book.roomAny}</option>
                {t.rooms.list
                  .map((r, i) => ({ r, meta: ROOMS_META[i] }))
                  .filter((x) => x.meta && !x.meta.hidden)
                  .sort((a, b) => (a.meta.order ?? 99) - (b.meta.order ?? 99))
                  .map(({ r }) => (
                    <option key={r.name} value={r.name}>{r.name}</option>
                  ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls} htmlFor="bk-msg">{t.book.message}</label>
            <textarea
              id="bk-msg"
              data-testid="booking-message"
              value={form.message}
              onChange={set("message")}
              placeholder={t.book.messagePh}
              rows={3}
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            type="submit"
            data-testid="booking-submit"
            disabled={soldOut || rangeError}
            className="mt-1 flex items-center justify-center gap-2 rounded-full bg-purpleink px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:bg-purpleink-deep active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-purpleink"
          >
            <Send className="h-4 w-4" />
            {soldOut ? (t.book.availSoldOutBtn || "Sold out for these dates") : t.book.submit}
          </button>

          <div className="flex items-center gap-3 py-0.5">
            <span className="h-px flex-1 bg-stone-300" />
            <span className="text-xs font-medium uppercase tracking-wider text-stone-400">
              {t.book.or}
            </span>
            <span className="h-px flex-1 bg-stone-300" />
          </div>

          <button
            type="button"
            onClick={handleWhatsApp}
            data-testid="booking-whatsapp"
            className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:brightness-105 active:scale-95"
          >
            <WhatsAppIcon className="h-5 w-5" />
            {t.book.chatDirect}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
